import { DoctorLayout } from "@/components/layout/doctor-layout";
import { useListOpenaiConversations, useCreateOpenaiConversation, useGetOpenaiConversation } from "@workspace/api-client-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquarePlus, Send, Loader2, Bot, AlertTriangle, Clock } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { getGetOpenaiConversationQueryKey, getListOpenaiConversationsQueryKey } from "@workspace/api-client-react";
import { AiConsentModal } from "@/components/ui/ai-consent-modal";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";
import { TeamCodeBadge } from "@/components/ui/same-team-badge";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function DoctorChat() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [consented, setConsented] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [viewerTeamCode, setViewerTeamCode] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    fetch(`${BASE}/api/user/profile-summary`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => setViewerTeamCode((me as { teamCode?: string | null } | null)?.teamCode ?? null))
      .catch(() => {});
  }, []);

  const { data: convos, isLoading: convosLoading } = useListOpenaiConversations();
  const createConvo = useCreateOpenaiConversation();
  const { data: activeConvo, isLoading: chatLoading } = useGetOpenaiConversation(
    activeId!,
    { query: { enabled: !!activeId, queryKey: getGetOpenaiConversationQueryKey(activeId!) } }
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeConvo?.messages, isStreaming]);

  const handleStartNew = async () => {
    createConvo.mutate({ data: { title: "New Clinical Query", userRole: "doctor" } }, {
      onSuccess: (newConvo) => {
        setActiveId(newConvo.id);
        queryClient.invalidateQueries({ queryKey: getListOpenaiConversationsQueryKey() });
      }
    });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !activeId || isStreaming) return;

    const userMessage = input.trim();
    setInput("");
    setIsStreaming(true);
    setRateLimited(false);

    const tempUserMsg = { id: Date.now(), conversationId: activeId, role: "user", content: userMessage, createdAt: new Date().toISOString() };
    const tempAssistantMsg = { id: Date.now() + 1, conversationId: activeId, role: "assistant", content: "", createdAt: new Date().toISOString() };

    queryClient.setQueryData(getGetOpenaiConversationQueryKey(activeId), (old: any) => {
      if (!old) return old;
      return { ...old, messages: [...old.messages, tempUserMsg, tempAssistantMsg] };
    });

    try {
      const response = await fetch(`/api/openai/conversations/${activeId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: userMessage, userRole: "doctor" })
      });

      if (response.status === 429) {
        setRateLimited(true);
        queryClient.invalidateQueries({ queryKey: getGetOpenaiConversationQueryKey(activeId) });
        return;
      }

      if (!response.body) throw new Error("No response body");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const dataStr = line.slice(6);
            if (!dataStr) continue;
            try {
              const data = JSON.parse(dataStr);
              if (data.done) break;
              if (data.content) {
                assistantContent += data.content;
                queryClient.setQueryData(getGetOpenaiConversationQueryKey(activeId), (old: any) => {
                  if (!old) return old;
                  const msgs = [...old.messages];
                  msgs[msgs.length - 1].content = assistantContent;
                  return { ...old, messages: msgs };
                });
              }
            } catch (_e) {}
          }
        }
      }
    } catch (_error) {
    } finally {
      setIsStreaming(false);
      queryClient.invalidateQueries({ queryKey: getGetOpenaiConversationQueryKey(activeId) });
    }
  };

  return (
    <DoctorLayout title="AI Clinical Assistant">
      {!consented && (
        <AiConsentModal variant="doctor" onConsented={() => setConsented(true)} />
      )}

      <div className="space-y-3">
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 text-xs text-indigo-800">
          <strong>Clinical decision support only.</strong> This AI assistant is grounded in GMC Good Medical Practice, NICE, BSR, and EULAR guidance. It does not replace clinical judgment, diagnosis, or individual patient prescribing decisions. Responses are logged for governance purposes.
        </div>

        <div className="flex h-[calc(100vh-240px)] bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 border-r border-slate-200 bg-slate-50 flex flex-col">
            <div className="p-4 border-b border-slate-200 space-y-2">
              <Button
                onClick={handleStartNew}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={createConvo.isPending}
              >
                {createConvo.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquarePlus className="h-4 w-4 mr-2" />}
                New Query
              </Button>
              {viewerTeamCode && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-500">Your team</span>
                  <TeamCodeBadge teamCode={viewerTeamCode} />
                </div>
              )}
            </div>
            <div className="flex items-start gap-2 px-4 py-3 border-b border-slate-200">
              <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-500 leading-snug">
                Queries are retained for 365 days as part of your professional record.
              </p>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {convosLoading ? (
                  <div className="p-4 text-center text-sm text-slate-500">Loading...</div>
                ) : convos?.length === 0 ? (
                  <div className="p-4 text-center text-sm text-slate-500">No previous queries</div>
                ) : (
                  convos?.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setActiveId(c.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm truncate transition-colors ${
                        activeId === c.id
                          ? "bg-indigo-100 text-indigo-900 font-medium"
                          : "text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {c.title}
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {!activeId ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-500">
                <Bot className="h-12 w-12 mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Consultant-Level AI Assistant</h3>
                <p className="max-w-md text-sm">
                  Ask consultant-level clinical questions on diagnostics, prescribing criteria, biosimilar switching, disease scoring thresholds, or guideline interpretation. Responses are grounded in GMC, NICE, BSR, EULAR, and ACR guidance.
                </p>
                <div className="mt-4 flex flex-wrap gap-2 justify-center max-w-md">
                  {[
                    "NICE criteria for starting a JAK inhibitor in RA",
                    "Pre-treatment TB screening for biologic therapy",
                    "DAS28 remission thresholds and treat-to-target",
                    "ANCA vasculitis induction regimen options",
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setInput(suggestion)}
                      className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-3 py-1.5 hover:bg-indigo-100 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6">
                  {chatLoading ? (
                    <div className="flex justify-center p-4"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
                  ) : activeConvo?.messages?.length === 0 ? (
                    <div className="text-center text-slate-500 mt-10">How can I assist you clinically today?</div>
                  ) : (
                    activeConvo?.messages?.map((msg, i) => (
                      <div key={msg.id || i} className={`flex gap-4 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                        {msg.role === "assistant" && (
                          <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0 mt-1">
                            <Bot className="h-5 w-5" />
                          </div>
                        )}
                        <div className={`max-w-[80%] rounded-2xl px-5 py-3.5 ${
                          msg.role === "user"
                            ? "bg-indigo-600 text-white rounded-tr-sm"
                            : "bg-slate-100 text-slate-800 rounded-tl-sm"
                        }`}>
                          <div className="whitespace-pre-wrap leading-relaxed text-sm">
                            {msg.content || (msg.role === "assistant" && isStreaming ? "..." : "")}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-4 border-t border-slate-200 bg-white space-y-2">
                  {rateLimited && (
                    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      Message limit reached (30 per hour). Please try again later.
                    </div>
                  )}
                  <form onSubmit={handleSend} className="flex gap-2 max-w-4xl mx-auto items-center">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Type or dictate a consultant-level clinical question..."
                      className="flex-1"
                      disabled={isStreaming}
                    />
                    <VoiceInputButton
                      tone="indigo"
                      size="md"
                      onTranscript={(chunk) => setInput((prev) => appendVoiceChunk(prev, chunk))}
                      disabled={isStreaming}
                      label="Dictate clinical question"
                    />
                    <Button
                      type="submit"
                      disabled={!input.trim() || isStreaming}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DoctorLayout>
  );
}
